import { useState, useEffect } from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, SparklesIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import propsService from '../../services/propsService';

/**
 * ValuePlaysInsight - Shows ML vs Sportsbook value plays for a game
 * Designed to sit beneath the GameCard tile for quick betting insights
 *
 * @param {string} sport - Sport type (nba, nhl, nfl)
 * @param {string} gameDate - Game date in YYYY-MM-DD format
 * @param {string} matchup - Matchup string like "PHX @ OKC"
 * @param {boolean} compact - Show compact view (for game cards)
 */
const ValuePlaysInsight = ({ sport, gameDate, matchup, compact = true }) => {
  const [valuePlays, setValuePlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchValuePlays = async () => {
      // Require valid sport and date
      if (!sport || !gameDate) {
        setLoading(false);
        return;
      }

      // Skip unsupported sports (ncaab, ncaaf, mlb don't have props)
      const supportedSports = ['nba', 'nhl', 'nfl'];
      if (!supportedSports.includes(sport.toLowerCase())) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get props for this matchup with value plays only
        const data = await propsService.getPropsForMatchup(sport, gameDate, matchup);

        // Filter for value plays (edge >= 1.5 or <= -1.5)
        const plays = (data.props || []).filter(p =>
          p.edge !== null && Math.abs(p.edge) >= 1.5
        ).sort((a, b) => Math.abs(b.edge) - Math.abs(a.edge));

        setValuePlays(plays);
      } catch (err) {
        // Only log actual errors, not expected failures (404, etc.)
        if (err?.response?.status !== 404) {
          console.error('Error fetching value plays:', err?.message || err);
        }
        setError('Unable to load');
      } finally {
        setLoading(false);
      }
    };

    fetchValuePlays();
  }, [sport, gameDate, matchup]);

  // Loading state
  if (loading) {
    return (
      <div className="mt-2 py-2 border-t border-gray-700/30">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <SparklesIcon className="w-3 h-3 animate-pulse" />
          <span>Loading insights...</span>
        </div>
      </div>
    );
  }

  // Error or no plays
  if (error || valuePlays.length === 0) {
    return null; // Don't show anything if no value plays
  }

  // Get the top value plays to show
  const topPlays = valuePlays.slice(0, expanded ? 6 : 2);
  const hasMore = valuePlays.length > 2;

  // Edge color helper
  const getEdgeColor = (edge) => {
    if (edge >= 3) return 'text-green-400';
    if (edge >= 1.5) return 'text-green-300';
    if (edge <= -3) return 'text-red-400';
    if (edge <= -1.5) return 'text-red-300';
    return 'text-gray-400';
  };

  // Edge background helper
  const getEdgeBg = (edge) => {
    if (edge >= 3) return 'bg-green-500/10 border-green-500/30';
    if (edge >= 1.5) return 'bg-green-500/5 border-green-500/20';
    if (edge <= -3) return 'bg-red-500/10 border-red-500/30';
    if (edge <= -1.5) return 'bg-red-500/5 border-red-500/20';
    return 'bg-gray-500/5 border-gray-500/20';
  };

  return (
    <div className="mt-2 pt-2 border-t border-gray-700/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <SparklesIcon className="w-3 h-3 text-amber-400" />
          <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">
            AI Value Plays
          </span>
        </div>
        {valuePlays.length > 0 && (
          <span className="text-[10px] text-gray-500">
            {valuePlays.length} found
          </span>
        )}
      </div>

      {/* Value Plays List */}
      <div className="space-y-1.5">
        {topPlays.map((play, idx) => (
          <div
            key={play.id || idx}
            className={`flex items-center justify-between px-2 py-1.5 rounded border ${getEdgeBg(play.edge)}`}
          >
            {/* Player & Stat */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-white truncate">
                  {play.player_name}
                </span>
                <span className="text-[10px] text-gray-400">
                  {play.stat_type}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-gray-500">
                  Line: {play.sb_line}
                </span>
                <span className="text-[10px] text-gray-500">
                  ML: {play.ml_prediction?.toFixed(1)}
                </span>
              </div>
            </div>

            {/* Edge & Direction */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className={`flex items-center gap-0.5 ${getEdgeColor(play.edge)}`}>
                {play.edge > 0 ? (
                  <ArrowTrendingUpIcon className="w-3 h-3" />
                ) : (
                  <ArrowTrendingDownIcon className="w-3 h-3" />
                )}
                <span className="text-xs font-bold">
                  {play.edge > 0 ? '+' : ''}{play.edge?.toFixed(1)}
                </span>
              </div>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                play.edge > 0
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {play.edge > 0 ? 'OVER' : 'UNDER'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Expand/Collapse Button */}
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 w-full flex items-center justify-center gap-1 text-[10px] text-gray-400 hover:text-gray-300 transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUpIcon className="w-3 h-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDownIcon className="w-3 h-3" />
              Show {valuePlays.length - 2} more
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ValuePlaysInsight;
